import { useState, useContext } from "react"
import { UserContext } from "../Context/UserContext"
import "../styles/mobileHitterList.css"
import ColorLabels from "./ColorLabels"
import HitterFilters from "./HitterFilters"

function MobileHitterList({
  data,
  searchTerm,
  onSearchChange,
  isActiveSub,
  navigateToHitterPage,
  filters,
  setFilters,
  availableTeams,
  activeFilterCount,
}) {
  const { state } = useContext(UserContext)
  const [openStatId, setOpenStatID] = useState(null)

  function expandHitterRow(statID) {
    setOpenStatID((prev) => (prev === statID ? null : statID))
  }

  function getTierInfo(rating) {
    if (rating >= 0.450) return { label: "Elite",     bg: "#E6F1FB", color: "#185FA5", bar: "#378ADD" }
    if (rating >= 0.400) return { label: "Strong",    bg: "#E1F5EE", color: "#0F6E56", bar: "#1D9E75" }
    if (rating >  0.333) return { label: "Favorable", bg: "#FAEEDA", color: "#854F0B", bar: "#F5C518" }
    if (rating >  0.230) return { label: "Average",   bg: "#D3D1C7", color: "#444441", bar: "#888780" }
    if (rating !== null && rating <= 0.230)
                         return { label: "Poor",      bg: "#FCEBEB", color: "#A32D2D", bar: "#E24B4A" }
    return               {        label: null,        bg: null,      color: null,      bar: "#888780" }
  }

  return (
    <div>
      <div className="searchBar">
        <label>Search A Hitter: </label>
        <input
          type="text"
          placeholder="Ex: Kris Bryant"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <ColorLabels />
      <HitterFilters
        filters={filters}
        setFilters={setFilters}
        teams={availableTeams}
        activeCount={activeFilterCount}
      />
      <div className="listContainer">
        {data.map((stat) => {
          const isOpen     = openStatId === stat.id
          const tier       = getTierInfo(stat.hit_matchup_rating)
          const teamStyles = { color: state.teamColors[stat.hitter_team].primary || "transparent" }

          const matchupRatingBg =
            stat.hit_matchup_rating >= 0.450 ? "lightblue"   :
            stat.hit_matchup_rating >= 0.400 ? "lightgreen"  :
            stat.hit_matchup_rating >  0.333 ? "lightyellow" :
            stat.hit_matchup_rating <= 0.230 ? "lightpink"   : "#d0d0d0"

          const matchupRatingColor =
            stat.hit_matchup_rating >= 0.450 ? "#0c447c" :
            stat.hit_matchup_rating >= 0.400 ? "#1a5c1a" :
            stat.hit_matchup_rating >  0.333 ? "#633806" :
            stat.hit_matchup_rating <= 0.230 ? "#791F1F" : "#2c2c2a"

          const matchupWOBABg =
            stat.hit_matchup_woba >= 0.400                                    ? "lightblue"   :
            stat.hit_matchup_woba  > 0.350 && stat.hit_matchup_woba < 0.400  ? "lightgreen"  :
            stat.hit_matchup_woba >= 0.315 && stat.hit_matchup_woba <= 0.350 ? "lightyellow" :
            stat.hit_matchup_woba <= 0.300                                    ? "lightpink"   : "transparent"

          const matchupISOBg =
            stat.hit_matchup_iso >= 0.275                                     ? "lightblue"   :
            stat.hit_matchup_iso  < 0.275 && stat.hit_matchup_iso >= 0.200   ? "lightgreen"  :
            stat.hit_matchup_iso  > 0.160 && stat.hit_matchup_iso  < 0.199   ? "lightyellow" :
            stat.hit_matchup_iso <= 0.150                                     ? "lightpink"   : "transparent"

          const matchupHCBg =
            stat.hit_matchup_hard_contact >= 50.0                                             ? "lightblue"   :
            stat.hit_matchup_hard_contact  < 50.0 && stat.hit_matchup_hard_contact >= 40.0   ? "lightgreen"  :
            stat.hit_matchup_hard_contact >= 33.0 && stat.hit_matchup_hard_contact  < 40.0   ? "lightyellow" :
            stat.hit_matchup_hard_contact <= 29.0                                             ? "lightpink"   : "transparent"

          const matchupFBBg =
            stat.hit_matchup_fb_pct >= 50.0                                         ? "lightblue"   :
            stat.hit_matchup_fb_pct  < 50.0 && stat.hit_matchup_fb_pct >= 40.0     ? "lightgreen"  :
            stat.hit_matchup_fb_pct  > 37.0 && stat.hit_matchup_fb_pct  < 40.0     ? "lightyellow" :
            stat.hit_matchup_fb_pct <= 30.0                                         ? "lightpink"   : "transparent"

          const upColor   = "rgba(0,100,0)"
          const downColor = "rgba(139,0,0)"
          const wOBAChangeColor  = stat.hit_woba_percent_change > 0 ? upColor : downColor
          const ISOChangeColor   = stat.hit_iso_percent_change > 0 ? upColor : downColor
          const HCChangeColor    = stat.hit_hard_contact_percent_change > 0 ? upColor : downColor
          const HRFBChangeColor  = stat.hit_hr_per_fb_percent_change > 0 ? upColor : downColor
          const FBChangeColor    = stat.hit_fb_percent_change > 0 ? upColor : downColor

          const arsenalStars = stat.pitch_overall_stars
          const arsenalPct   = stat.pitch_overall_arsenal_pct
          const hasArsenal   = arsenalStars != null || arsenalPct != null

          const arsenalLeft = hasArsenal ? (
            <div className="arsenalPills">
              <span className="arsenalLabel">Pitch Arsenal</span>
              <span className="arsenalStarPill">
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} style={{ color: i < (arsenalStars ?? 0) ? "#f5a623" : "#ccc" }}>★</span>
                ))}
              </span>
              {stat.pitch_overall_low_data ? (
                <span
                  className="arsenalNoPitchData"
                  title={`Only ${stat.pitch_overall_data_coverage_pct?.toFixed(0)}% of arsenal has enough PA`}
                >
                  No Pitch Data
                </span>
              ) : (
                <span className="arsenalPctPill">
                  {arsenalPct != null ? `${Number(arsenalPct).toFixed(1)}%` : "—"}
                </span>
              )}
            </div>
          ) : (
            <div className="arsenalPills">
              <span className="arsenalLabel">Pitch Arsenal</span>
              <span className="arsenalPending">No Pitch Arsenal Report</span>
            </div>
          )

          return (
            <ul key={stat.id} className="mobileList">
              <li className="listRow" style={{ borderLeft: `4px solid ${tier.bar}` }}>

                {/* ── Top: name/pitcher | rating ── */}
                <div className="cardTop">
                  <div className="cardLeft">
                    <div style={teamStyles} className="hitterNameAndTeam">
                      <span className="hitterName">{stat.hitter_name}</span>
                      <span className="batHand">({stat.bat_hand})</span>
                      <span className="hitterTeam">{stat.hitter_team}</span>
                    </div>
                    <span className="pitcherDetails">vs {stat.pitcher_name} ({stat.throw_hand})</span>
                  </div>
                  <span
                    className="matchupRating"
                    style={{ backgroundColor: matchupRatingBg, color: matchupRatingColor }}
                  >
                    {stat.hit_matchup_rating?.toFixed(3)}
                  </span>
                </div>

                {/* ── Divider ── */}
                <div className="cardDivider" />

                {/* ── Bottom: arsenal | tier ── */}
                <div className="cardBottom">
                  {arsenalLeft}
                  {tier.label && (
                    <span className="tierBadge" style={{ backgroundColor: tier.bg, color: tier.color }}>
                      {tier.label}
                    </span>
                  )}
                </div>

                {/* ── Expand toggle ── */}
                <span className={isOpen ? "arrow open" : "arrow"} onClick={() => expandHitterRow(stat.id)}>
                  ▼
                </span>

                {/* ── Expanded content ── */}
                {isOpen && (
                  <div className="expandedContent">

                    {/* Hitter Stats — 4-col grid */}
                    <div className="expandedSection">
                      <div className="secLabel">Hitter Stats</div>
                      <div className="hitterStatsGrid">
                        <div className="statCell"><div className="statValue">{stat.hit_pa}</div><div className="statLabel">PA</div></div>
                        <div className="statCell"><div className="statValue">{stat.hit_wrc_plus}</div><div className="statLabel">wRC+</div></div>
                        <div className="statCell"><div className="statValue">{stat.hit_woba}</div><div className="statLabel">wOBA</div></div>
                        <div className="statCell"><div className="statValue">{stat.hit_iso}</div><div className="statLabel">ISO</div></div>
                        <div className="statCell"><div className="statValue">{stat.hit_hard_contact_pct}%</div><div className="statLabel">Hard Ct</div></div>
                        <div className="statCell"><div className="statValue">{stat.hit_fb_pct}%</div><div className="statLabel">FB%</div></div>
                        <div className="statCell"><div className="statValue">{stat.hit_hr_per_fb}%</div><div className="statLabel">HR/FB</div></div>
                      </div>
                    </div>

                    {/* Matchup Stats — 5-col grid */}
                    <div className="expandedSection">
                      <div className="secLabel">Matchup Stats</div>
                      <div className="matchupStatsGrid">
                        <div className="statCell"><div className="matchupChip" style={{ background: matchupWOBABg }}>{stat.hit_matchup_woba?.toFixed(3)}</div><div className="statLabel">wOBA</div></div>
                        <div className="statCell"><div className="matchupChip" style={{ background: matchupISOBg }}>{stat.hit_matchup_iso?.toFixed(3)}</div><div className="statLabel">ISO</div></div>
                        <div className="statCell"><div className="matchupChip" style={{ background: matchupHCBg }}>{stat.hit_matchup_hard_contact?.toFixed(1)}%</div><div className="statLabel">Hard Ct</div></div>
                        <div className="statCell"><div className="matchupChip" style={{ background: matchupFBBg }}>{stat.hit_matchup_fb_pct?.toFixed(1)}%</div><div className="statLabel">FB Rate</div></div>
                        <div className="statCell"><div className="matchupChip">{stat.hit_matchup_hr_per_fb?.toFixed(1)}%</div><div className="statLabel">HR/FB</div></div>
                      </div>

                      {/* Pct changes — same 5-col alignment, % Chg label below */}
                      {isActiveSub && (
                        <div className="pctChangeGrid">
                          <div className="statCell"><div className="pctValue" style={{ color: wOBAChangeColor }}>{stat.hit_woba_percent_change > 0 ? "+" : ""}{stat.hit_woba_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                          <div className="statCell"><div className="pctValue" style={{ color: ISOChangeColor }}>{stat.hit_iso_percent_change > 0 ? "+" : ""}{stat.hit_iso_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                          <div className="statCell"><div className="pctValue" style={{ color: HCChangeColor }}>{stat.hit_hard_contact_percent_change > 0 ? "+" : ""}{stat.hit_hard_contact_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                          <div className="statCell"><div className="pctValue" style={{ color: FBChangeColor }}>{stat.hit_fb_percent_change > 0 ? "+" : ""}{stat.hit_fb_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                          <div className="statCell"><div className="pctValue" style={{ color: HRFBChangeColor }}>{stat.hit_hr_per_fb_percent_change > 0 ? "+" : ""}{stat.hit_hr_per_fb_percent_change?.toFixed(1)}%</div><div className="statLabel">% Chg</div></div>
                        </div>
                      )}
                    </div>

                    <button
                      className="moreMatchupButton"
                      onClick={() => navigateToHitterPage(stat?.hitter_name, stat?.hitter_id)}
                    >
                      More on this matchup
                    </button>
                  </div>
                )}
              </li>
            </ul>
          )
        })}
      </div>
    </div>
  )
}

export default MobileHitterList